import { useState, useRef } from "react";
import { X, Upload, Image, Calendar, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import DateCalendar from "../../DateCalendar";

interface AddPhotosModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: any) => void;
}

const treatmentTypes = [
  { id: "dermal-lips", name: "Dermal Fillers - Lips" },
  { id: "dermal-cheeks", name: "Dermal Fillers - Cheeks" },
  { id: "botox-forehead", name: "Botox - Forehead" },
  { id: "botox-crows", name: "Botox - Crow's Feet" },
  { id: "lip-filler", name: "Lip Filler" },
  { id: "chemical-peel", name: "Chemical Peel" },
  { id: "microneedling", name: "Microneedling" },
];

export default function AddPhotosModal({
  isOpen,
  onClose,
  onSave,
}: AddPhotosModalProps) {
  const [beforeImage, setBeforeImage] = useState<string | null>(null);
  const [afterImage, setAfterImage] = useState<string | null>(null);
  const [treatment, setTreatment] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState("");
  const [isDateOpen, setIsDateOpen] = useState(false);

  const beforeInputRef = useRef<HTMLInputElement>(null);
  const afterInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "before" | "after"
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === "before") {
          setBeforeImage(reader.result as string);
        } else {
          setAfterImage(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (type: "before" | "after") => {
    if (type === "before") {
      setBeforeImage(null);
      if (beforeInputRef.current) beforeInputRef.current.value = "";
    } else {
      setAfterImage(null);
      if (afterInputRef.current) afterInputRef.current.value = "";
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleSave = () => {
    const photoData = {
      beforeImage,
      afterImage,
      treatment,
      date: selectedDate,
      notes,
    };
    onSave?.(photoData);
    // Reset form
    setBeforeImage(null);
    setAfterImage(null);
    setTreatment("");
    setSelectedDate(new Date());
    setNotes("");
    onClose();
  };

  const isValid = beforeImage || afterImage;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-purple-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#2d2d2d]">Add Photos</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Photo Upload Section */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-3">
              Upload Photos
            </label>
            <div className="grid grid-cols-2 gap-4">
              {/* Before Photo */}
              <div>
                <p className="text-sm font-medium text-[#704aaf] mb-2">Before</p>
                {beforeImage ? (
                  <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-[#e8e0f0]">
                    <img
                      src={beforeImage}
                      alt="Before"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeImage("before")}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => beforeInputRef.current?.click()}
                    className="aspect-[4/3] border-2 border-dashed border-[#e8e0f0] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#704aaf] hover:bg-purple-50/30 transition-colors"
                  >
                    <Upload className="w-8 h-8 text-[#704aaf] mb-2" />
                    <p className="text-sm text-[#6b6b6b]">Upload Before</p>
                    <p className="text-xs text-[#9b9b9b]">Click to browse</p>
                  </div>
                )}
                <input
                  ref={beforeInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, "before")}
                  className="hidden"
                />
              </div>

              {/* After Photo */}
              <div>
                <p className="text-sm font-medium text-[#704aaf] mb-2">After</p>
                {afterImage ? (
                  <div className="relative aspect-[4/3] rounded-lg overflow-hidden border border-[#e8e0f0]">
                    <img
                      src={afterImage}
                      alt="After"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeImage("after")}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => afterInputRef.current?.click()}
                    className="aspect-[4/3] border-2 border-dashed border-[#e8e0f0] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[#704aaf] hover:bg-purple-50/30 transition-colors"
                  >
                    <Upload className="w-8 h-8 text-[#704aaf] mb-2" />
                    <p className="text-sm text-[#6b6b6b]">Upload After</p>
                    <p className="text-xs text-[#9b9b9b]">Click to browse</p>
                  </div>
                )}
                <input
                  ref={afterInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, "after")}
                  className="hidden"
                />
              </div>
            </div>
          </div>

          {/* Treatment Type */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">
              Treatment Type
            </label>
            <Select value={treatment} onValueChange={setTreatment}>
              <SelectTrigger className="w-full border-gray-200 h-[42px] text-sm">
                <SelectValue placeholder="Select treatment type" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[1200]">
                {treatmentTypes.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">
              Date Taken
            </label>
            <Popover open={isDateOpen} onOpenChange={setIsDateOpen}>
              <PopoverTrigger asChild>
                <button className="w-full flex items-center gap-2 px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] hover:border-purple-300 transition-colors text-left">
                  <Calendar className="w-4 h-4 text-[#704aaf]" />
                  <span>{formatDate(selectedDate)}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[1200] bg-white border border-gray-200 shadow-xl rounded-xl">
                <DateCalendar
                  selectedDate={selectedDate}
                  onSelectDate={(date) => {
                    setSelectedDate(date);
                    setIsDateOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this photo set..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isValid}
              className="flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#5f3d9a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Image className="w-4 h-4" />
              Save Photos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}